/**
 * `InlineEditValue` — one-step inline editor for a single policy field
 * (terms of service URL, privacy URL, returns URL, return window).
 *
 * Reused by three surfaces (same visual, same save path):
 *   1. Passing policy row (ChecksSection) — label + current value + Edit.
 *   2. Missing-row Add in ChecksSection — Edit opens prefilled from
 *      the scan suggestion.
 *   3. "What we found" row inside FixDrawer and NextStepCard — the
 *      drawer "Edit full agent card" link stays as a secondary way out.
 *
 * Behaviour:
 *   - Enter saves; Esc cancels; focus returns to the Edit button on
 *     close.
 *   - Non-blocking "This isn't on {domain}" warning when the saved URL
 *     is not on the merchant's own domain.
 *   - Save error → input stays open, inline coral "Couldn't save: …".
 */

import { useEffect, useId, useRef, useState } from 'react';

import { useC } from '../../lib/theme.js';
import { monoFont, sansFont } from './fonts.js';

export type InlineEditKind = 'url' | 'days';

export interface InlineEditValueProps {
  readonly label: string;
  readonly value: string | null;
  readonly kind: InlineEditKind;
  readonly site_domain: string;
  /** Off-domain URLs this returns true for get no warning (e.g. a Calendly link). */
  readonly allowOffDomain?: (url: string) => boolean;
  readonly placeholder?: string;
  /** Optional prefill — e.g. the scan-suggested URL for a missing row. */
  readonly prefill?: string | null;
  readonly save_hint?: string;
  readonly onSave: (next: string) => Promise<void>;
  /** When provided, the component shows a secondary "Edit full agent card" link. */
  readonly onOpenFullEditor?: () => void;
  /** When provided, replaces the default "Edit" button label. */
  readonly editLabel?: string;
  /** When provided, forces the editor open on mount. Used by NextStepCard
   *  so the row renders in "editing" state without an extra click. */
  readonly initiallyEditing?: boolean;
  readonly data_testid?: string;
}

function formatDisplay(value: string | null, kind: InlineEditKind): string {
  if (value === null || value.trim().length === 0) return 'Not set';
  if (kind === 'days') {
    const n = parseInt(value, 10);
    if (!Number.isNaN(n)) return `${n} ${n === 1 ? 'day' : 'days'}`;
  }
  return value;
}

function isOnDomain(url: string, domain: string): boolean {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    const d = domain.replace(/^www\./, '');
    return host === d || host.endsWith(`.${d}`);
  } catch {
    return true; // best-effort — don't warn on parse failure
  }
}

export function InlineEditValue({
  label,
  value,
  kind,
  site_domain,
  allowOffDomain,
  placeholder,
  prefill,
  save_hint,
  onSave,
  onOpenFullEditor,
  editLabel,
  initiallyEditing = false,
  data_testid,
}: InlineEditValueProps): JSX.Element {
  const C = useC();
  const inputId = useId();
  const [editing, setEditing] = useState<boolean>(initiallyEditing);
  const [draft, setDraft] = useState<string>(() => {
    if (initiallyEditing) {
      if (value !== null && value.trim().length > 0) return value;
      return prefill ?? '';
    }
    return value ?? '';
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const editBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (editing && inputRef.current !== null) {
      inputRef.current.focus();
      try { inputRef.current.select(); } catch { /* select may throw on number inputs */ }
    }
  }, [editing]);

  function startEdit(): void {
    setDraft(() => {
      if (value !== null && value.trim().length > 0) return value;
      return prefill ?? '';
    });
    setError(null);
    setEditing(true);
  }

  function cancel(): void {
    setEditing(false);
    setError(null);
    setTimeout(() => editBtnRef.current?.focus(), 0);
  }

  async function doSave(): Promise<void> {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      setError('Enter a value.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed);
      setEditing(false);
      setTimeout(() => editBtnRef.current?.focus(), 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  // Warning line — only when the saved value is a URL on a different domain.
  const offDomainWarning: string | null = (() => {
    if (kind !== 'url') return null;
    const current = editing ? draft.trim() : (value ?? '');
    if (current.length === 0) return null;
    if (!current.startsWith('http')) return null;
    if (allowOffDomain !== undefined && allowOffDomain(current)) return null;
    return isOnDomain(current, site_domain) ? null : `This isn't on ${site_domain}.`;
  })();

  const displayValue = formatDisplay(value, kind);
  const isUrl = kind === 'url' && typeof value === 'string' && value.startsWith('http');

  if (!editing) {
    return (
      <div
        data-testid={data_testid}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          fontFamily: sansFont,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {label.length > 0 ? (
            <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: '0.14em', color: C.muted, textTransform: 'uppercase' }}>
              {label}
            </div>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {isUrl && value !== null ? (
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: C.text,
                  fontFamily: monoFont,
                  fontSize: 13,
                  textDecoration: 'underline',
                  textDecorationColor: C.dim,
                  textUnderlineOffset: '3px',
                  wordBreak: 'break-all',
                }}
              >
                {value}
              </a>
            ) : (
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 13,
                  color: value === null || value.trim().length === 0 ? C.muted : C.text,
                }}
              >
                {displayValue}
              </span>
            )}
          </div>
          {offDomainWarning !== null ? (
            <div style={{ fontSize: 11, color: C.amberFg, marginTop: 2 }}>
              {offDomainWarning}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          ref={editBtnRef}
          onClick={startEdit}
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            padding: '6px 12px',
            minHeight: 32,
            background: 'transparent',
            color: C.text,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          {editLabel ?? (value === null || value.trim().length === 0 ? 'Add' : 'Edit')}
        </button>
      </div>
    );
  }

  // Editing state
  return (
    <form
      data-testid={data_testid}
      onSubmit={(e) => { e.preventDefault(); void doSave(); }}
      style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: sansFont }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {label.length > 0 ? (
          <label
            htmlFor={inputId}
            style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: '0.14em', color: C.muted, textTransform: 'uppercase' }}
          >
            {label}
          </label>
        ) : null}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input
          id={inputId}
          ref={inputRef}
          type={kind === 'days' ? 'number' : 'url'}
          inputMode={kind === 'days' ? 'numeric' : 'url'}
          min={kind === 'days' ? 1 : undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          placeholder={placeholder}
          disabled={saving}
          aria-label={label.length > 0 ? label : undefined}
          style={{
            flex: '1 1 240px',
            minWidth: 180,
            fontFamily: monoFont,
            fontSize: 13,
            padding: '8px 10px',
            background: C.bg,
            color: C.text,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            minHeight: 36,
          }}
        />
        {kind === 'days' ? (
          <span style={{ fontFamily: monoFont, fontSize: 12, color: C.muted }}>days</span>
        ) : null}
        <button
          type="submit"
          disabled={saving || draft.trim().length === 0}
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            fontWeight: 500,
            padding: '7px 14px',
            minHeight: 36,
            background: 'transparent',
            color: C.text,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            padding: '7px 14px',
            minHeight: 36,
            background: 'transparent',
            color: C.muted,
            border: '1px solid transparent',
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
      {offDomainWarning !== null && error === null ? (
        <div style={{ fontSize: 11, color: C.amberFg }}>{offDomainWarning}</div>
      ) : null}
      {error !== null ? (
        <div role="alert" style={{ fontSize: 12, color: C.coral }}>
          Couldn&apos;t save: {error}
        </div>
      ) : save_hint !== undefined && !saving ? (
        <div style={{ fontSize: 11, color: C.muted }}>{save_hint}</div>
      ) : null}
      {onOpenFullEditor !== undefined ? (
        <button
          type="button"
          onClick={onOpenFullEditor}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            marginTop: 2,
            color: C.muted,
            fontFamily: monoFont,
            fontSize: 11,
            textDecoration: 'underline',
            textDecorationColor: C.dim,
            textUnderlineOffset: '3px',
            cursor: 'pointer',
            alignSelf: 'flex-start',
          }}
        >
          Edit full agent card
        </button>
      ) : null}
    </form>
  );
}
